<template>
    <div class="EntityDicts">
        <WCard
            class="EntityDicts"
            :loading="isLoading"
        >
            <DataGrid
                ref="dataGrid"
                class="DataGrid"
                v-bind="dataGridConfig"
                :loading="isLoading"
                :columns="cols"
                :rows="rows"
                @onRowClick="handleRowClick"
            />
        </WCard>

        <ModalRequest
            :opened="modal === modals.REMOVE"
            :title="$t('entities.modals.removeDict.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            @close="handleCleanModal"
            @onAccept="removeDict"
        />

        <Modal
            :opened="[modals.CREATE, modals.UPDATE].includes(modal)"
            :title="$t(`entities.dicts.${ modals.CREATE === modal ? 'createDict' : 'editDict'}`)"
            #body
            @close="handleCleanModal"
        >
            <DictForm
                :value="(node || {}).data"
                @onReset="handleCleanModal"
                @onSubmit="handleDictForm"
            />
        </Modal>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifyError, notifySuccess, sleep, task,
} from '@/api'
import { DataGrid, ModalRequest, Modal } from '@/components'
import { entityMixin } from '@/mixins'
import { TYPES } from '@/entities/fields'
import { DictForm } from './components'

const COLS = [
    {
        headerName: 'Key',
        field: 'key',
    },
    {
        headerName: 'Name',
        field: 'name',
    },
    {
        headerName: '# entries',
        field: 'entries',
    },
    {
        headerName: 'Locked',
        field: 'locked',
        type: TYPES.BOOL,
        width: 100,
        suppressSizeToFit: true,
    },
]

const MODALS = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    REMOVE: 'REMOVE',
}

export default {
    name: 'EntityDicts',
    components: {
        DataGrid,
        ModalRequest,
        Modal,
        DictForm,
    },
    mixins: [entityMixin],
    props: {
        entityKey: VueTypes.string,
    },
    entity () {
        return {
            buttons: [{
                iconLeft: 'plus-circle',
                text: this.$t('entities.addDict'),
                onClick: () => this.handleRequest(this.modals.CREATE),
            }],
        }
    },
    data () {
        return {
            cols: COLS,
            rows: undefined,

            modals: MODALS,
            modal: false,

            node: undefined,
            isLoading: false,

            dataGridConfig: {
                hasInputFilter: false,
                filterable: false,
                sideBar: false,
                cleanFiltersButton: false,
                editable: false,
                fitResponsive: true,
                contextMenu: this.contextMenu,
            },
        }
    },
    mounted () {
        this.getData()
    },
    methods: {
        contextMenu ({ node, api }) {
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`

            return [
                'copy',
                'paste',
                'separator',
                ...([
                    {
                        name: this.$t('entities.actions.edit'),
                        action: () => this.handleRequest(MODALS.UPDATE, node),
                        icon: renderIcon('edit'),
                    },
                    {
                        name: this.$t('entities.actions.delete'),
                        action: () => this.handleRequest(MODALS.REMOVE, node),
                        icon: renderIcon('trash'),
                    },
                ]),
                'separator',
                {
                    name: this.$t('entities.actions.export'),
                    action: () => api.exportDataAsCsv({ columnSeparator: '\t' }),
                    icon: renderIcon('download'),
                },
            ]
        },
        handleCleanModal () {
            this.node = undefined
            this.modal = undefined
        },
        handleRequest (modal, node) {
            this.modal = modal
            if (node) this.node = node
        },
        handleDictForm () {
            const { data } = this.node || {}
            this.getData()
            if ((data || {}).id) notifySuccess(this.$t('entities.modals.updateDictSuccess'))()
            else notifySuccess(this.$t('entities.modals.createDictSuccess'))()
            this.handleCleanModal()
        },
        handleRowClick ({ data }) {
            this.$router.push({ name: 'DictsDetail', params: { dictKey: data.key } })
        },
        async getData () {
            this.isLoading = true
            apiRequest(Api().dictionaries.list())
                .then(({ data }) => (this.rows = data || []))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async removeDict () {
            this.isLoading = true
            const { data } = this.node
            const params = { dictKey: data.key }
            apiRequest(Api().dictionaries.delete(params))
                .then(task(this.handleCleanModal))
                .then(sleep(600))
                .then(task(() => this.getData()))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
    },
}
</script>
<style lang="scss" scoped>
.DataGrid {
	@include admin-screen-height(88px);

    &::v-deep .ag-row:hover {
        cursor: pointer;
    }
}
</style>
